import { FC } from 'react';
import { BodyText, Card, CircleButton, Icon, withStyles } from 'vopay';

import Dialog from '#shared/components/Dialog/Dialog';
import styles from './EnhancedVerificationCardDialog.module.scss';
import Feature from '../Feature';
import { useTranslation } from 'react-i18next';

import { Button } from '#shared/components/UI';

type EnhancedVerificationCardDialogProps = {
  onCancel: () => void;
  // Captured from useHostNavigate() by the opener — the dialog renders under
  // the DialogProvider, above the HostNavigationProvider, so the context
  // can't be read here directly.
  hostNavigate?: (path: string, state?: unknown) => void;
};

const cn = withStyles(styles);

const EnhancedVerificationCardDialog: FC<EnhancedVerificationCardDialogProps> = ({ onCancel, hostNavigate }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.InteracVerification.EnhancedVerificationCardDialog',
  });

  return (
    <Card className={cn('enhancedVerificationCardDialog')}>
      <Dialog.Title level="3">{t('title', { defaultValue: 'Enhanced Verification' })}</Dialog.Title>

      <div className={cn('closeButton')}>
        <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
      </div>

      <BodyText className={cn('description')}>
        {t('desc', {
          defaultValue:
            'Confirm ownership and registration details retrieved from card processor records for this contact.',
        })}
      </BodyText>

      <div className={cn('features')}>
        <Feature
          icon={<Icon icon="badgeBlueGradient" width="35px" height="35px" color="none" />}
          title={t('CardHolderName.title', { defaultValue: 'Card Holder Name' })}
          desc={t('CardHolderName.desc', {
            defaultValue: 'See the registered name tied to the card.',
          })}
        />

        <Feature
          icon={<Icon icon="cardBlueGradient" width="35px" height="35px" color="none" />}
          title={t('CardAndCardBrand.title', { defaultValue: 'Card & Card brand' })}
          desc={t('CardAndCardBrand.desc', { defaultValue: 'Identify if the card is personal, business, or prepaid.' })}
        />

        <Feature
          icon={<Icon icon="currencyExchangeBlueGradient" width="35px" height="35px" color="none" />}
          title={t('EligibilityToSendReceive.title', { defaultValue: 'Eligibility to send & receive funds' })}
          desc={t('EligibilityToSendReceive.desc', {
            defaultValue: 'Verify if the account accepts & processes fund transfers.',
          })}
        />

        <Feature
          icon={<Icon icon="accessTimeBlueGradient" width="35px" height="35px" color="none" />}
          title={t('EstimatedFundAvailability.title', { defaultValue: 'Estimated fund availability' })}
          desc={t('EstimatedFundAvailability.desc', {
            defaultValue: 'Get an estimated time for when the transferred funds will be available',
          })}
        />
      </div>

      <Button
        className={cn('button')}
        colorScheme="primary"
        onClick={() => {
          onCancel();

          // Link-out via the host's onNavigate; the dialog just closes when the
          // host can't navigate. (dropped router state: ticketSubject)
          hostNavigate?.('/create-tickets', {
            ticketSubject: t('ticketSubject', { defaultValue: 'Enquiry about Enhanced Card verification' }),
          });
        }}
      >
        {t('contactButton', { defaultValue: 'Contact Support' })}
      </Button>
    </Card>
  );
};

export default EnhancedVerificationCardDialog;
